import { useEffect, useRef } from 'react';

interface Props {
  onAuthStart: () => void;
  error?: string | null;
}

declare global {
  interface Window {
    YaAuthSuggest?: {
      init: (
        oauthParams: { client_id: string; response_type: string; redirect_uri: string; state?: string },
        origin: string,
        options: {
          view: string;
          parentId: string;
          buttonSize?: string;
          buttonView?: string;
          buttonTheme?: string;
          buttonBorderRadius?: string;
          buttonIcon?: string;
        }
      ) => Promise<{ handler: () => Promise<void> }>;
    };
  }
}

/** Текущая схема оформления системы. */
function prefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function YandexOAuthLogin({ onAuthStart, error }: Props) {
  const clientId = import.meta.env.VITE_YANDEX_CLIENT_ID;
  const webappUrl = import.meta.env.VITE_WEBAPP_URL || window.location.origin;
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);
  const buttonInitializedRef = useRef(false);

  useEffect(() => {
    if (!clientId || scriptLoadedRef.current) return;

    // Загружаем SDK Яндекс ID
    const script = document.createElement('script');
    script.src = 'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js';
    script.async = true;
    script.onload = () => {
      console.log('[YandexOAuth] SDK loaded');
      scriptLoadedRef.current = true;
      initYandexButton();
    };
    script.onerror = () => {
      console.error('[YandexOAuth] Failed to load SDK');
    };
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [clientId]);

  const initYandexButton = () => {
    if (!window.YaAuthSuggest || !containerRef.current || !clientId || buttonInitializedRef.current) {
      return;
    }

    buttonInitializedRef.current = true;

    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('yandex_oauth_state', state);

    const redirectUri = `${webappUrl}/auth/yandex/callback`;

    console.log('[YandexOAuth] Initializing button', { clientId, redirectUri });

    window.YaAuthSuggest.init(
      {
        client_id: clientId,
        response_type: 'code', // Используем code flow для безопасности
        redirect_uri: redirectUri,
        state,
      },
      webappUrl,
      {
        view: 'button',
        parentId: 'yandex-suggest-button',
        buttonSize: 'l',
        buttonView: 'main',
        buttonTheme: 'dark',
        buttonBorderRadius: '14',
        buttonIcon: 'ya',
      }
    )
      .then(({ handler }) => {
        console.log('[YandexOAuth] Button initialized, setting up handler');
        onAuthStart();
        return handler();
      })
      .catch((err) => {
        console.error('[YandexOAuth] Initialization error:', err);
        buttonInitializedRef.current = false;
      });
  };

  if (!clientId) {
    return null; // Не отображать кнопку если Яндекс OAuth не настроен
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', width: '100%' }}>
      <div
        id="yandex-suggest-button"
        ref={containerRef}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          borderRadius: 14,
          // Кнопка Яндекса всегда чёрная (это их бренд), и на тёмной карточке
          // она сливается с фоном. Тему виджета переопределить нельзя,
          // поэтому отделяем её тонким кольцом — как это делают с чёрной
          // кнопкой Apple на тёмных экранах.
          boxShadow: prefersDark() ? '0 0 0 1px var(--app-border-strong)' : 'none',
        }}
      />
      {error && (
        <p style={{ color: 'var(--app-danger)', fontSize: 14, margin: 0, fontWeight: 500 }}>{error}</p>
      )}
    </div>
  );
}
